import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect } from 'react'
import {
  AuftragOption,
  AuftragOptionen,
  BefehlFormAuftrag,
  BefehlFormField,
  ReadOnlyText
} from '../Generic'
import type { RadioChipOption } from '../../form'
import {
  BetriebsstelleField,
  BetriebsstellenartField,
  Input,
  RadioChipGroup
} from '../../form'
import type { Befehl9FormData } from './schema'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag9_40FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
}

export default function Auftrag9_40Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag9_40FormProps>) {
  const formMethods = useFormContext<Befehl9FormData>()
  const { control, formState, watch, setValue } = formMethods
  const { errors } = formState
  const { t } = useTranslation('ui-befehle')

  const selector = watch('auftrag_9_40.selector')
  const sel9_47Type = watch('auftrag_9_40.feld_9_47_type')
  const sel9_48Type = watch('auftrag_9_40.feld_9_48_type')
  const sel9_43Betriebsstellenart = watch(
    'auftrag_9_40.feld_9_43_zugmeldestelle.art'
  )
  const x44Betriebsstellenart = watch(
    'auftrag_9_40.feld_9_44_zugmeldestelle.art'
  )

  const clearConflictingX47Fields = useCallback(
    (newType: string) => {
      if (newType === 'km') {
        setValue('auftrag_9_40.feld_9_47_2_signal', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newType === 'signal') {
        setValue('auftrag_9_40.feld_9_47_1_km', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue]
  )

  const clearConflictingX48Fields = useCallback(
    (newType: string) => {
      if (newType === 'km') {
        setValue('auftrag_9_40.feld_9_48_2_signal', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newType === 'signal') {
        setValue('auftrag_9_40.feld_9_48_1_km', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue]
  )

  const onX47TypeChange = useCallback(
    (newValue: string) => {
      setValue('auftrag_9_40.feld_9_47_type', newValue as '' | 'km' | 'signal')
      clearConflictingX47Fields(newValue)
    },
    [setValue, clearConflictingX47Fields]
  )

  const onX48TypeChange = useCallback(
    (newValue: string) => {
      setValue('auftrag_9_40.feld_9_48_type', newValue as '' | 'km' | 'signal')
      clearConflictingX48Fields(newValue)
    },
    [setValue, clearConflictingX48Fields]
  )

  useEffect(() => {
    if (selector === '') {
      setValue('auftrag_9_40.feld_9_43_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
      setValue('auftrag_9_40.feld_9_44_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    } else if (selector === 'in') {
      setValue('auftrag_9_40.feld_9_44_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    }
  }, [selector, setValue])

  return (
    <BefehlFormAuftrag
      auftragKennung="9.40"
      fieldName="auftrag_9_40.enabled"
      readOnly={true}
      disabled={false}
      testId={testId}
    >
      <div className="flex items-baseline gap-0.5 flex-wrap">
        <span data-testid={`${testId}-text`}>{t('auftraege.9_40.label')}</span>
      </div>
      <div className="flex-1">
        <div className="w-full">
          <div className="flex flex-col gap-1">
            <div className="flex flex-col @container">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right flex justify-start @[400px]:justify-end items-baseline">
                  <BefehlFormField
                    name="auftrag_9_40.selector"
                    control={control}
                    label=""
                    readOnly={readOnly}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-9-43-44-selector`}
                    render={({ field, isDisabled }) => {
                      const options: RadioChipOption[] = [
                        {
                          value: 'zwischen',
                          label: t('auftraege.9_40.bestimmte_woerter.zwischen')
                        },
                        {
                          value: 'in',
                          label: t('auftraege.9_40.bestimmte_woerter.in')
                        }
                      ]
                      return (
                        <RadioChipGroup
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          options={options}
                          disabled={isDisabled}
                          readOnly={readOnly}
                          showOnlySelected={readOnly}
                          testId={`${testId}-feld-9-43-44-selector-inner`}
                        />
                      )
                    }}
                  />
                </div>
                <div className="flex-1">
                  {selector === '' ? (
                    <div className="border border-cool-gray-200 px-1 py-1">
                      <ReadOnlyText
                        value={
                          readOnly
                            ? ''
                            : t('auftraege.9_40.placeholder_select_in_zwischen')
                        }
                        label=""
                        variant="solid"
                        activateEmptyWarning={activateEmptyFieldWarnings}
                        data-testid={`${testId}-feld-9-43-44-placeholder`}
                      />
                    </div>
                  ) : (
                    <BorderedBox
                      data-testid={`${testId}-feld-9-43-bordered-box`}
                      footer={
                        <span data-testid={`${testId}-feld-9-43-label`}>
                          {t('auftraege.9_40.9_43_label')}
                        </span>
                      }
                      className={
                        selector === 'zwischen' ? '@[400px]:border-b-0' : ''
                      }
                    >
                      <ZugmeldestelleWrapper
                        betriebsstellenartField={
                          <BefehlFormField
                            name="auftrag_9_40.feld_9_43_zugmeldestelle.art"
                            control={control}
                            label="Betriebsstellenart"
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-9-43-art`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstellenartField
                                {...field}
                                label={t(
                                  'auftraege.9_40.betriebsstellenart_label'
                                )}
                                variant="solid"
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                data-testid={`${testId}-feld-9-43-art-inner`}
                                selectClassName="w-full"
                              />
                            )}
                          />
                        }
                        bestriebsstelleField={
                          <BefehlFormField
                            name="auftrag_9_40.feld_9_43_zugmeldestelle.name"
                            control={control}
                            label={t('auftraege.9_40.zugmeldestelle')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            data-testid={`${testId}-feld-9-43-name`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstelleField
                                {...field}
                                label={t('auftraege.9_40.zugmeldestelle')}
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                typ={sel9_43Betriebsstellenart}
                                data-testid={`${testId}-feld-9-43-name-inner`}
                              />
                            )}
                          />
                        }
                      />
                    </BorderedBox>
                  )}
                </div>
              </div>

              <div
                className={`${selector === 'zwischen' ? 'block @container' : 'hidden @container'}`}
              >
                <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                  <div className="mt-0.5 @[400px]:w-8 @[400px]:text-right">
                    <span data-testid={`${testId}-text-und`}>
                      {t('auftraege.9_40.und')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <BorderedBox
                      data-testid={`${testId}-feld-9-44-bordered-box`}
                      footer={
                        <span data-testid={`${testId}-feld-9-44-label`}>
                          {t('auftraege.9_40.9_44_label')}
                        </span>
                      }
                      className="@[400px]:border-t-0"
                    >
                      <ZugmeldestelleWrapper
                        betriebsstellenartField={
                          <BefehlFormField
                            name="auftrag_9_40.feld_9_44_zugmeldestelle.art"
                            control={control}
                            label="Betriebsstellenart"
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-9-44-art`}
                            render={({ field, isDisabled, isReadOnly }) => {
                              return (
                                <BetriebsstellenartField
                                  {...field}
                                  label={t(
                                    'auftraege.9_40.betriebsstellenart_label'
                                  )}
                                  variant="solid"
                                  errors={errors}
                                  disabled={isDisabled}
                                  readOnly={isReadOnly}
                                  data-testid={`${testId}-feld-9-44-art-inner`}
                                  selectClassName="w-full"
                                />
                              )
                            }}
                          />
                        }
                        bestriebsstelleField={
                          <BefehlFormField
                            name="auftrag_9_40.feld_9_44_zugmeldestelle.name"
                            control={control}
                            label={t('auftraege.9_40.zugmeldestelle')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            data-testid={`${testId}-feld-9-44-name`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstelleField
                                {...field}
                                label={t('auftraege.9_40.zugmeldestelle')}
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                typ={x44Betriebsstellenart}
                                data-testid={`${testId}-feld-9-44-name-inner`}
                              />
                            )}
                          />
                        }
                      />
                    </BorderedBox>
                  </div>
                </div>
              </div>
            </div>
            <div className="@container">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right">
                  <span data-testid={`${testId}-von`}>
                    {t('auftraege.9_40.von_label')}
                  </span>
                </div>
                <div className="flex-1">
                  <BefehlFormField
                    name="auftrag_9_40.feld_9_47_type"
                    control={control}
                    label=""
                    readOnly={false}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-9-47-selector`}
                    render={({ field, isDisabled }) => (
                      <AuftragOptionen
                        name="auftrag_9_40.feld_9_47_type"
                        value={field.value}
                        onChange={(newValue) => onX47TypeChange(newValue)}
                        readOnly={readOnly}
                        disabled={isDisabled}
                        errors={errors}
                        data-testid={`${testId}-feld-9-47-selector-options`}
                      >
                        <AuftragOption value="km" label="9.47.1 km">
                          <div className="flex flex-col sm:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_9_40.feld_9_47_1_km"
                                control={control}
                                label={t('auftraege.9_40.km_label')}
                                readOnly={readOnly}
                                disabled={sel9_47Type !== 'km'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-9-47-1`}
                                render={({ field: kmField, isDisabled }) => (
                                  <Input
                                    {...kmField}
                                    label={t('auftraege.9_40.km_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-9-47-1-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                        <AuftragOption value="signal" label="9.47.2 Signal">
                          <div className="flex flex-col sm:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_9_40.feld_9_47_2_signal"
                                control={control}
                                label={t('auftraege.9_40.signal_label')}
                                readOnly={readOnly}
                                disabled={sel9_47Type !== 'signal'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-9-47-2`}
                                render={({
                                  field: signalField,
                                  isDisabled
                                }) => (
                                  <Input
                                    {...signalField}
                                    label={t('auftraege.9_40.signal_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-9-47-2-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                      </AuftragOptionen>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="@container">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right">
                  <span data-testid={`${testId}-text-bis`}>
                    {t('auftraege.9_40.bis_label')}
                  </span>
                </div>
                <div className="flex-1">
                  <BefehlFormField
                    name="auftrag_9_40.feld_9_48_type"
                    control={control}
                    label=""
                    readOnly={false}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-9-48-selector`}
                    render={({ field, isDisabled }) => (
                      <AuftragOptionen
                        name="auftrag_9_40.feld_9_48_type"
                        value={field.value}
                        onChange={(newValue) => onX48TypeChange(newValue)}
                        readOnly={readOnly}
                        disabled={isDisabled}
                        errors={errors}
                        data-testid={`${testId}-feld-9-48-selector-options`}
                      >
                        <AuftragOption value="km" label="9.48.1 km">
                          <div className="flex flex-col sm:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_9_40.feld_9_48_1_km"
                                control={control}
                                label={t('auftraege.9_40.km_label')}
                                readOnly={readOnly}
                                disabled={sel9_48Type !== 'km'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-9-48-1`}
                                render={({ field: kmField, isDisabled }) => (
                                  <Input
                                    {...kmField}
                                    label={t('auftraege.9_40.km_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-9-48-1-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                        <AuftragOption value="signal" label="9.48.2 Signal">
                          <div className="flex flex-col sm:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_9_40.feld_9_48_2_signal"
                                control={control}
                                label={t('auftraege.9_40.signal_label')}
                                readOnly={readOnly}
                                disabled={sel9_48Type !== 'signal'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-9-48-2`}
                                render={({
                                  field: signalField,
                                  isDisabled
                                }) => (
                                  <Input
                                    {...signalField}
                                    label={t('auftraege.9_40.signal_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-9-48-2-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                      </AuftragOptionen>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BefehlFormAuftrag>
  )
}
